import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { AccessRequestItemStatus } from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useAdminAccessRequests, useReviewAccessRequestItem } from '../../api/admin';

const TABS: { value: AccessRequestItemStatus | 'ALL'; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'ALL', label: 'All' },
];

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function AccessRequestsPage() {
  const { isPlatformAdmin } = useAuth();
  const [tab, setTab] = useState<AccessRequestItemStatus | 'ALL'>('PENDING');
  const requests = useAdminAccessRequests(tab === 'ALL' ? undefined : tab);
  const review = useReviewAccessRequestItem();
  const toast = useToast();

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  async function decide(requestId: string, itemId: string, decision: 'APPROVED' | 'REJECTED') {
    try {
      await review.mutateAsync({ requestId, itemId, input: { decision } });
      toast.success(decision === 'APPROVED' ? 'Menu granted.' : 'Request rejected.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not review this item.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 960 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Access Requests</h1>

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {TABS.map((t) => (
          <Button
            key={t.value}
            type="button"
            variant={tab === t.value ? 'primary' : 'secondary'}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      <Card>
        {requests.isPending ? (
          <PageState kind="loading" message="Loading…" />
        ) : requests.isError ? (
          <PageState
            kind="error"
            message={requests.error.message}
            onRetry={() => void requests.refetch()}
          />
        ) : requests.data.items.length === 0 ? (
          <PageState kind="empty" title="Nothing here" />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
            {requests.data.items.map((req) => (
              <div
                key={req.id}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  paddingBottom: 'var(--space-3)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 'var(--space-2)',
                  }}
                >
                  <strong>
                    {req.tenantName}{' '}
                    <span style={{ color: 'var(--color-text-muted)' }}>({req.tenantSlug})</span>
                  </strong>
                  <span
                    style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
                  >
                    {when(req.createdAt)}
                  </span>
                </div>
                {req.note ? (
                  <p style={{ margin: 'var(--space-1) 0', color: 'var(--color-text-muted)' }}>
                    “{req.note}”
                  </p>
                ) : null}
                <ul
                  style={{
                    margin: 'var(--space-2) 0 0',
                    padding: 0,
                    listStyle: 'none',
                    display: 'grid',
                    gap: 'var(--space-1)',
                  }}
                >
                  {req.items.map((item) => (
                    <li
                      key={item.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                    >
                      <span style={{ minWidth: 160 }}>{item.menuLabel}</span>
                      <span className="ne-badge">{item.status}</span>
                      {item.status === 'PENDING' ? (
                        <>
                          <Button
                            disabled={review.isPending}
                            onClick={() => void decide(req.id, item.id, 'APPROVED')}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="secondary"
                            disabled={review.isPending}
                            onClick={() => void decide(req.id, item.id, 'REJECTED')}
                          >
                            Reject
                          </Button>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
